import { Resolution } from '../../../utilities/types';
import { ResolutionInput } from '../FramingChart/types';
import { isValidResolution } from '../FramingChart/validators/validateResolution';

/**
 * Creates a Resolution object from width/height values if both are defined and valid.
 * Returns null if either value is null, undefined, NaN, or non-positive.
 *
 * @param width - The width value (can be null or undefined)
 * @param height - The height value (can be null or undefined)
 * @returns A Resolution object if both values are valid positive finite numbers, null otherwise
 */
export const createResolutionFromValues = (
  width: number | null | undefined,
  height: number | null | undefined
): Resolution | null => {
  // Check that both values exist and are valid positive finite numbers
  if (width != null && height != null && isValidResolution({ width, height })) {
    return { width, height };
  }
  return null;
};

/**
 * Creates a ResolutionInput object from width/height values.
 * For each input value, sets the value to null if it is undefined, NaN, or non-positive.
 *
 * @param width - The width value (can be null or undefined)
 * @param height - The height value (can be null or undefined)
 * @returns A ResolutionInput object
 */
export const createResolutionInputFromValues = (
  width: number | null | undefined,
  height: number | null | undefined
): ResolutionInput => {
  // Set each value to null if it is undefined, NaN, or non-positive
  const validWidth =
    width != null && isFinite(width) && width > 0 ? width : null;
  const validHeight =
    height != null && isFinite(height) && height > 0 ? height : null;
  return { width: validWidth, height: validHeight };
};
